import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import {
  BreakGlassContext,
  BREAK_GLASS_CATEGORIES as CATEGORIES,
  BREAK_GLASS_MIN_JUSTIFICATION,
  validateBreakGlass,
} from '../../services/breakGlass';

interface BreakGlassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (ctx: BreakGlassContext) => void;
  action: string;
  // Extra line under the main notice, e.g. the read-only and time limit terms.
  note?: string;
}

export const BreakGlassModal: React.FC<BreakGlassModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  note,
}) => {
  const [category, setCategory] = useState('');
  const [justification, setJustification] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCategory('');
      setJustification('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isValid = validateBreakGlass({ category, justification }) === null;

  const handleConfirm = () => {
    if (isValid) onConfirm({ category, justification: justification.trim() });
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="break-glass-title"
        style={{
          background: 'white', borderRadius: '8px', maxWidth: '520px', width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)', overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fff9ec'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={24} color="#f59e0b" />
            <div>
              <h2 id="break-glass-title" style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#92400e' }}>
                Break glass — elevated access
              </h2>
              <p style={{ fontSize: '12px', color: '#b45309', margin: '2px 0 0' }}>
                System admin operation
              </p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={18} color="#92400e" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <p style={{ margin: '0 0 20px', color: '#374151', fontSize: '14px', lineHeight: '1.6' }}>
            You are about to <strong>{action}</strong>. This is a system admin operation and will be permanently
            recorded with your identity, timestamp, and justification.
          </p>
          {note && (
            <p style={{ margin: '-8px 0 20px', color: '#92400e', fontSize: '13px', lineHeight: '1.5' }}>{note}</p>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="break-glass-category" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              Reason category <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              id="break-glass-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px',
                border: '1px solid #d1d5db', borderRadius: '6px',
                fontSize: '14px', outline: 'none', background: 'white',
                boxSizing: 'border-box' as const
              }}
            >
              <option value="">Select a reason…</option>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label htmlFor="break-glass-justification" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              Justification <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ fontWeight: '400', color: '#9ca3af', marginLeft: '6px' }}>(min {BREAK_GLASS_MIN_JUSTIFICATION} characters)</span>
            </label>
            <textarea
              id="break-glass-justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Describe the specific reason for this elevated access…"
              rows={3}
              style={{
                width: '100%', padding: '8px 12px',
                border: '1px solid #d1d5db', borderRadius: '6px',
                fontSize: '14px', outline: 'none', resize: 'vertical',
                fontFamily: 'inherit', boxSizing: 'border-box' as const
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f9fafb'
        }}>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>
            Logged at critical severity. Cannot be deleted.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px', background: 'white', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isValid}
              style={{
                padding: '8px 16px',
                background: isValid ? '#d97706' : '#e5e7eb',
                color: isValid ? 'white' : '#9ca3af',
                border: 'none', borderRadius: '6px',
                cursor: isValid ? 'pointer' : 'not-allowed',
                fontSize: '14px', fontWeight: '600'
              }}
            >
              Confirm and proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
